import { useCallback, useEffect, useMemo, useState } from 'react';
import { getBookingCalendar, updateBookingStatus } from '../../api';
import { useAircraft } from '../../aircraft/AircraftContext';
import { BookingCalendarBooking, BookingCalendarMonth } from '../../types';
import { DateFormat } from '../../utils/DateFormat';
import { getApiErrorMessage } from '../../utils/apiError';
import { getColorsHidden, onColorsPrefChange } from '../../utils/ColorPrefs';
import { useFeedback } from '../../hooks/useFeedback';
import PilotBadge from '../PilotBadge/PilotBadge';
import ModerateBookingModal from '../ModerateBookingModal/ModerateBookingModal';
import './BookingCalendar.css';

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

type Props = {
  dateFrom: string;
  dateTo: string;
  onRangeChange: (dateFrom: string, dateTo: string) => void;
  currentUserId: number;
  refreshKey?: number;
  onBookingStatusChanged?: () => void;
  onBookClick?: () => void;
};

const toISODate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseISODate = (value: string) => {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const hexToRgba = (hex: string, alpha: number) => {
  const raw = hex.replace('#', '');
  if (raw.length !== 6) return `rgba(149, 165, 166, ${alpha})`;
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const BookingCalendar = ({
  dateFrom,
  dateTo,
  onRangeChange,
  currentUserId,
  refreshKey = 0,
  onBookingStatusChanged,
  onBookClick,
}: Props) => {
  const { showError, feedbackNode } = useFeedback();
  const { aircraftId } = useAircraft();
  const today = useMemo(() => toISODate(new Date()), []);
  const initial = parseISODate(dateFrom || today);
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth() + 1);
  const [data, setData] = useState<BookingCalendarMonth | null>(null);
  const [selectingStart, setSelectingStart] = useState(true);
  const [loading, setLoading] = useState(false);
  const [moderating, setModerating] = useState<{
    booking: BookingCalendarBooking;
    date: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [hideColors, setHideColors] = useState(getColorsHidden);

  useEffect(() => onColorsPrefChange(() => setHideColors(getColorsHidden())), []);

  const loadCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBookingCalendar(viewYear, viewMonth);
      setData(res.data);
    } catch (error) {
      setData(null);
      showError(getApiErrorMessage(error, 'Не удалось загрузить календарь'));
    }
    setLoading(false);
  }, [viewYear, viewMonth, showError]);

  useEffect(() => {
    void loadCalendar();
  }, [loadCalendar, refreshKey, aircraftId]);

  const dayMap = useMemo(() => {
    const map = new Map<string, BookingCalendarMonth['days'][number]>();
    data?.days.forEach((d) => map.set(d.date, d));
    return map;
  }, [data]);

  const bookingsByDate = useMemo(() => {
    const map = new Map<string, BookingCalendarBooking[]>();
    if (!data) return map;
    data.bookings.forEach((b) => {
      let cur = parseISODate(b.date_from);
      const end = parseISODate(b.date_to);
      while (cur <= end) {
        const key = toISODate(cur);
        const list = map.get(key) || [];
        list.push(b);
        map.set(key, list);
        cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
      }
    });
    return map;
  }, [data]);

  const cells = useMemo(() => {
    const first = new Date(viewYear, viewMonth - 1, 1);
    const startOffset = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(viewYear, viewMonth, 0).getDate();
    const result: Array<{ date: string | null }> = [];

    for (let i = 0; i < startOffset; i += 1) {
      result.push({ date: null });
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      result.push({
        date: toISODate(new Date(viewYear, viewMonth - 1, day)),
      });
    }
    while (result.length % 7 !== 0) {
      result.push({ date: null });
    }
    return result;
  }, [viewYear, viewMonth]);

  const shiftMonth = (delta: number) => {
    const next = new Date(viewYear, viewMonth - 1 + delta, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth() + 1);
  };

  const isInRange = (date: string) => {
    if (!dateFrom || !dateTo) return false;
    const a = dateFrom <= dateTo ? dateFrom : dateTo;
    const b = dateFrom <= dateTo ? dateTo : dateFrom;
    return date >= a && date <= b;
  };

  const handleDayClick = (date: string) => {
    if (selectingStart || !dateFrom) {
      onRangeChange(date, date);
      setSelectingStart(false);
      return;
    }

    if (date < dateFrom) {
      onRangeChange(date, dateFrom);
    } else {
      onRangeChange(dateFrom, date);
    }
    setSelectingStart(true);
  };

  const handleModerate = async (status: 'approved' | 'rejected') => {
    if (!moderating) return;
    setSubmitting(true);
    try {
      await updateBookingStatus(moderating.booking.id, status);
      setModerating(null);
      await loadCalendar();
      onBookingStatusChanged?.();
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка обновления статуса'));
    }
    setSubmitting(false);
  };

  return (
    <div className="booking-calendar">
      {feedbackNode}
      <div className="booking-calendar-toolbar">
        <button type="button" className="cal-nav-btn" onClick={() => shiftMonth(-1)}>
          ‹
        </button>
        <h3>
          {MONTHS[viewMonth - 1]} {viewYear}
        </h3>
        <button type="button" className="cal-nav-btn" onClick={() => shiftMonth(1)}>
          ›
        </button>
      </div>

      <p className="booking-calendar-hint">
        {dateFrom && dateTo && selectingStart
          ? dateFrom === dateTo
            ? `Выбрано: ${DateFormat(dateFrom)}`
            : `Выбрано: ${DateFormat(dateFrom)} → ${DateFormat(dateTo)}`
          : selectingStart || !dateFrom
            ? 'Кликните дату начала периода'
            : `Начало: ${DateFormat(dateFrom)} · кликните дату окончания`}
      </p>

      <div className="booking-calendar-weekdays">
        {WEEKDAYS.map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      <div className={`booking-calendar-grid${loading ? ' loading' : ''}`}>
        {cells.map((cell, idx) => {
          if (!cell.date) {
            return <div key={`empty-${idx}`} className="cal-day empty" />;
          }

          const meta = dayMap.get(cell.date);
          const color = hideColors
            ? '#cfd8dc'
            : meta?.color || '#cfd8dc';
          const inRange = isInRange(cell.date);
          const isStart = cell.date === dateFrom;
          const isEnd = cell.date === dateTo;
          const isToday = cell.date === today;
          const dayBookings = (bookingsByDate.get(cell.date) || []).filter(
            (b) => b.status !== 'rejected'
          );
          const watermark =
            hideColors && meta?.owner_name ? meta.owner_name.trim() : '';

          return (
            <div
              key={cell.date}
              className={[
                'cal-day',
                inRange ? 'in-range' : '',
                isStart || isEnd ? 'endpoint' : '',
                isToday ? 'today' : '',
                watermark ? 'has-watermark' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{
                backgroundColor: hexToRgba(color, inRange ? 0.5 : 0.2),
                borderColor: color,
              }}
              onClick={() => handleDayClick(cell.date!)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleDayClick(cell.date!);
                }
              }}
            >
              {watermark && (
                <span className="cal-day-watermark" aria-hidden>
                  {watermark}
                </span>
              )}
              <span className="cal-day-num">{Number(cell.date.slice(-2))}</span>

              {dayBookings.length > 0 && (
                <div className="cal-day-bookings">
                  {dayBookings.map((b) => {
                    const canModerate =
                      b.status === 'pending' && b.week_owner_id === currentUserId;

                    return (
                      <button
                        key={b.id}
                        type="button"
                        className={`cal-booking-chip ${b.status}${canModerate ? ' actionable' : ''}`}
                        style={{
                          backgroundColor: hideColors
                            ? '#95a5a6'
                            : b.pilot_color || '#95a5a6',
                        }}
                        title={`${b.pilot}: ${b.status}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canModerate) {
                            setModerating({ booking: b, date: cell.date! });
                          }
                        }}
                      >
                        {b.pilot}
                        {b.status === 'pending' ? '?' : ''}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {data && data.pilots.length > 0 && !hideColors && (
        <div className="booking-calendar-legend">
          <span className="legend-title">Недели:</span>
          {data.pilots.map((p) => (
            <PilotBadge key={p.id} name={p.name} color={p.color} />
          ))}
        </div>
      )}

      <div className="booking-calendar-footer">
        <button type="button" className="cal-book-btn" onClick={onBookClick}>
          Забронировать
        </button>
      </div>

      {moderating && (
        <ModerateBookingModal
          booking={moderating.booking}
          date={moderating.date}
          submitting={submitting}
          onClose={() => setModerating(null)}
          onApprove={() => handleModerate('approved')}
          onReject={() => handleModerate('rejected')}
        />
      )}
    </div>
  );
};

export default BookingCalendar;
